import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Calendar, Clock, Search, MoreVertical, X,
  Check, Edit2, Trash2, Plus, AlertCircle, RefreshCw, Video, ChevronDown, ChevronUp,
  FileText, Download
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PendingAppointmentRequests from '../components/PendingAppointmentRequests';
import { getToken } from '../utils/auth';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fetchPendingAppointmentRequests } from '../api/appointmentsApi';
import {
  isAppointmentsRelatedPush,
  toDatetimeLocalValue,
  fromDatetimeLocalValue,
  defaultDurationMinutes,
  computeEndFromStartLocal,
  appointmentAuthHeaders,
  APPOINTMENT_TITLE_MAX_LENGTH,
  APPOINTMENT_NOTES_MAX_LENGTH,
  APPOINTMENT_TITLE_CUSTOM,
  APPOINTMENT_TITLE_PRESETS,
  resolveAppointmentTitleMode,
} from '../utils/appointmentHelpers';
import {
  appointmentTypeOf,
  isVideoAppointmentType,
  canJoinVideoAppointment,
  startZoomForAppointment,
} from '../utils/zoomSession';
import Modal, { DialogHeader, DialogFooter, DialogBody } from '../components/ui/Modal';
import DropdownPanel from '../components/ui/DropdownPanel';
import { AnimatedNumber } from '../motion';

const APPOINTMENT_SAVE_ERRORS = {
  patient_not_found: 'Selected patient was not found.',
  provider_not_found: 'Clinician was not found.',
  location_not_found: 'Location was not found.',
  scheduled_by_user_not_found: 'Scheduler user was not found.',
  invalid_time_range: 'End time must be after start time.',
  scheduling_conflict: 'That time overlaps another appointment for this clinician. Pick a different slot.',
};

const ACTION_MENU_WIDTH = 192;
const ACTION_MENU_MAX_HEIGHT = 280;
const ACTION_MENU_GAP = 8;
const VIEWPORT_MENU_MARGIN = 8;

function computeActionMenuPosition(rect) {
  const top = Math.round(rect.bottom + ACTION_MENU_GAP);
  const spaceBelow = window.innerHeight - top - VIEWPORT_MENU_MARGIN;
  const maxHeight = Math.min(
    ACTION_MENU_MAX_HEIGHT,
    Math.max(1, spaceBelow)
  );

  let left = Math.round(rect.right - ACTION_MENU_WIDTH);
  const minLeft = VIEWPORT_MENU_MARGIN;
  const maxLeft = Math.round(window.innerWidth - ACTION_MENU_WIDTH - VIEWPORT_MENU_MARGIN);
  left = Math.min(Math.max(left, minLeft), maxLeft);

  return { top, left, width: ACTION_MENU_WIDTH, maxHeight };
}

const AppointmentsDashboard = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, userType } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const highlightRequestId = searchParams.get('pendingRequest');
  const isClinicianUser = String(userType || '').toLowerCase() === 'clinician';
  const isAdminUser = String(userType || '').toLowerCase() === 'admin';

  const buildUrl = useCallback((baseUrl, path, hasId = false) => {
    const cleanBase = baseUrl.replace(/\/+$/, '');
    const cleanPath = path.replace(/^\/+|\/+$/g, '');
    return hasId ? `${cleanBase}/${cleanPath}` : `${cleanBase}/${cleanPath}/`;
  }, []);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [currentClinician, setCurrentClinician] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [editedStatus, setEditedStatus] = useState('');
  const [actionMenu, setActionMenu] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedPatients, setExpandedPatients] = useState({});
  const [transcriptModal, setTranscriptModal] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [joiningSessionId, setJoiningSessionId] = useState(null);

  const statusFilterOptions = [
    { id: 'active', label: 'Upcoming' },
    { id: 'Completed', label: 'Completed / Expired' },
    { id: 'Cancelled', label: 'Cancelled' },
    { id: 'No-Show', label: 'No-Show' },
    { id: 'all', label: 'All statuses' },
  ];

  const matchesStatusFilter = useCallback((appt, filter) => {
    const status = appt?.status;
    if (!status || status === 'Requested') return false;
    if (filter === 'all') return true;
    if (filter === 'active') {
      return (
        status !== 'Completed' &&
        status !== 'Cancelled' &&
        status !== 'No-Show' &&
        status !== 'No-show'
      );
    }
    if (filter === 'No-Show') {
      return status === 'No-Show' || status === 'No-show';
    }
    return status === filter;
  }, []);

  const [formData, setFormData] = useState({
    title: 'New Consultation',
    titleMode: 'New Consultation',
    appointmentType: 'In-Person',
    startTime: '',
    endTime: '',
    duration: 30,
    PMRN: '',
    provider_id: '',
    confirmation_notes: '',
  });

  const statusOptions = ["Confirmed", "Pending", "Completed", "Cancelled", "No-Show"];
  const appointmentTypes = ["In-Person", "Online", "Emergency"];
  const titleOptions = APPOINTMENT_TITLE_PRESETS;

  const openTranscriptViewer = useCallback(
    async (appointment) => {
      setTranscriptModal({
        appointment,
        loading: true,
        error: null,
        data: null
      });
      try {
        const token = getToken();
        if (!token) throw new Error('Not signed in');
        const url = buildUrl(
          API_BASE_URL,
          `zoom-video/appointment/${appointment.id}/artifacts`,
          true
        );
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) {
          let detail = `HTTP ${response.status}`;
          try {
            const errBody = await response.json();
            if (errBody.detail) detail = typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail);
          } catch {
                      }
          throw new Error(detail);
        }
        const data = await response.json();
        setTranscriptModal({
          appointment,
          loading: false,
          error: null,
          data
        });
      } catch (e) {
        setTranscriptModal({
          appointment,
          loading: false,
          error: e.message || 'Could not load transcript',
          data: null
        });
      }
    },
    [API_BASE_URL, buildUrl]
  );

  const closeTranscriptModal = useCallback(() => setTranscriptModal(null), []);

  const downloadTranscriptFile = useCallback(() => {
    if (!transcriptModal?.data?.fullTranscript) return;
    const text = transcriptModal.data.fullTranscript;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    const safeName = (transcriptModal.data.sessionName || 'session').replace(/[^\w.-]+/g, '_');
    a.href = URL.createObjectURL(blob);
    a.download = `visit-transcript-${transcriptModal.appointment.id}-${safeName}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [transcriptModal]);

  const statusColors = {
    Confirmed: "bg-green-100 text-green-800 border border-green-200",
    Scheduled: "bg-green-100 text-green-800 border border-green-200",
    Pending: "bg-yellow-100 text-yellow-800 border border-yellow-200",
    Requested: "bg-amber-100 text-amber-800 border border-amber-200",
    Completed: "bg-slate-100 text-slate-700 border border-slate-200",
    Cancelled: "bg-red-100 text-red-800 border border-red-200",
    "No-Show": "bg-blue-100 text-blue-800 border border-blue-200",
    "No-show": "bg-blue-100 text-blue-800 border border-blue-200",
  };

  useEffect(() => {
    if (!actionMenu) return undefined;
    const onPointerDown = (e) => {
      if (e.target.closest('[data-appointment-action-menu]')) return;
      if (e.target.closest('[data-action-menu-trigger]')) return;
      setActionMenu(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [actionMenu]);

  useEffect(() => {
    if (!actionMenu) return undefined;
    const id = actionMenu.id;
    const update = () => {
      const el = document.querySelector(`[data-action-menu-trigger="${id}"]`);
      if (!el) {
        setActionMenu(null);
        return;
      }
      const next = computeActionMenuPosition(el.getBoundingClientRect());
      setActionMenu((prev) => (prev && prev.id === id ? { id, ...next } : prev));
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [actionMenu]);

  useEffect(() => {
    if (showModal) setActionMenu(null);
  }, [showModal]);

  useEffect(() => {
    if (showDeleteConfirm || transcriptModal) setActionMenu(null);
  }, [showDeleteConfirm, transcriptModal]);

  useEffect(() => {
    if (actionMenu && !appointments.some((a) => a.id === actionMenu.id)) {
      setActionMenu(null);
    }
  }, [actionMenu, appointments]);

  const fetchAppointments = useCallback(async () => {
    try {
      setIsLoading(true);
      setActionMenu(null);
      const token = getToken();
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const url = buildUrl(API_BASE_URL, 'appointments/with-relations', false);
      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setAppointments(data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      showToast('Failed to fetch appointments', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [API_BASE_URL, buildUrl, showToast]);

  const fetchPendingRequests = useCallback(async () => {
    try {
      setPendingLoading(true);
      const token = getToken();
      if (!token) return;
      const data = await fetchPendingAppointmentRequests(API_BASE_URL, token);
      setPendingRequests(data);
    } catch (error) {
      setPendingRequests([]);
    } finally {
      setPendingLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onSwMessage = (event) => {
      if (event?.data?.type !== 'WEB_PUSH_RECEIVED') return;
      const pushPayload = event?.data?.payload || {};
      if (!isAppointmentsRelatedPush(pushPayload)) return;
      fetchAppointments();
      fetchPendingRequests();
    };
    navigator.serviceWorker.addEventListener('message', onSwMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', onSwMessage);
    };
  }, [fetchAppointments, fetchPendingRequests]);

  const fetchPatients = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const url = buildUrl(API_BASE_URL, 'patients', false);
      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setPatients(data);
    } catch (error) {
      console.error('Error fetching patients:', error);
      showToast('Failed to fetch patients', 'error');
    }
  }, [API_BASE_URL, buildUrl, showToast]);

  const clinicianDisplayName = useMemo(() => {
    const c = currentClinician || clinicians[0];
    if (!c) return '';
    return (
      c.name ||
      `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
      `Clinician ${c.id}`
    );
  }, [currentClinician, clinicians]);

  const lockedProviderId = currentClinician?.id || '';

  const fetchClinicians = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) return;

      if (isClinicianUser && user?.user_id) {
        const url = buildUrl(API_BASE_URL, `clinicians/user/${user.user_id}`, true);
        const response = await fetch(url, {
          headers: appointmentAuthHeaders(token),
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        const list = data ? [data] : [];
        setClinicians(list);
        setCurrentClinician(data || null);
        if (data?.id) {
          setFormData((prev) => ({
            ...prev,
            provider_id: prev.provider_id || data.id,
          }));
        }
        return;
      }

      if (!isAdminUser) {
        setClinicians([]);
        setCurrentClinician(null);
        return;
      }

      const url = buildUrl(API_BASE_URL, 'clinicians', false);
      const response = await fetch(url, {
        headers: appointmentAuthHeaders(token),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setClinicians(Array.isArray(data) ? data : []);
      setCurrentClinician(null);
    } catch (error) {
      console.error('Error fetching clinicians:', error);
      showToast('Failed to fetch clinicians', 'error');
    }
  }, [API_BASE_URL, buildUrl, isAdminUser, isClinicianUser, showToast, user?.user_id]);

  useEffect(() => {
    fetchAppointments();
    fetchPatients();
    fetchClinicians();
    fetchPendingRequests();
  }, [fetchAppointments, fetchPatients, fetchClinicians, fetchPendingRequests]);

  useEffect(() => {
    if (!highlightRequestId) return;
    const t = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      next.delete('pendingRequest');
      setSearchParams(next, { replace: true });
    }, 8000);
    return () => window.clearTimeout(t);
  }, [highlightRequestId, searchParams, setSearchParams]);

  const visibleAppointments = appointments.filter((appt) =>
    matchesStatusFilter(appt, statusFilter)
  );

  const activeAppointments = appointments.filter((appt) =>
    matchesStatusFilter(appt, 'active')
  );

  const getStatusCount = (status) => {
    if (status === 'Completed') {
      return appointments.filter((appt) => appt.status === 'Completed').length;
    }
    if (status === 'Cancelled') {
      return appointments.filter((appt) => appt.status === 'Cancelled').length;
    }
    if (status === 'No-Show') {
      return appointments.filter(
        (appt) => appt.status === 'No-Show' || appt.status === 'No-show'
      ).length;
    }
    return activeAppointments.filter((appt) => appt.status === status).length;
  };

  const filteredAppointments = visibleAppointments.filter(apt => {
    const searchLower = searchTerm.toLowerCase();
    return (
      !searchTerm ||
      (apt.patient_name && apt.patient_name.toLowerCase().includes(searchLower)) ||
      (apt.provider_name && apt.provider_name.toLowerCase().includes(searchLower)) ||
      (apt.status && apt.status.toLowerCase().includes(searchLower)) ||
      (appointmentTypeOf(apt).toLowerCase().includes(searchLower))
    );
  });

  const statusFilterLabel =
    statusFilterOptions.find((opt) => opt.id === statusFilter)?.label || 'Appointments';

  const appointmentsByPatient = filteredAppointments.reduce((acc, appt) => {
    const patientName = appt.patient_name || 'Unknown Patient';
    if (!acc[patientName]) {
      acc[patientName] = [];
    }
    acc[patientName].push(appt);
    return acc;
  }, {});

  const patientEntries = Object.entries(appointmentsByPatient).map(([patientName, appts]) => {
    const sortedAppointments = [...appts].sort(
      (a, b) => new Date(b.startTime) - new Date(a.startTime)
    );

    return {
      patientName,
      appointments: sortedAppointments,
      nextAppointment: sortedAppointments[0]
    };
  });
  const handleRowSelect = (id) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === filteredAppointments.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredAppointments.map(apt => apt.id));
    }
  };

  const startEditingStatus = (appointment) => {
    setEditingStatusId(appointment.id);
    setEditedStatus(appointment.status);
  };

  const cancelEditingStatus = () => {
    setEditingStatusId(null);
    setEditedStatus('');
  };

  const saveStatus = async (appointmentId) => {
    try {
      const token = getToken();
      if (!token) {
        showToast('Not signed in', 'error');
        return;
      }
      const url = `${buildUrl(API_BASE_URL, `appointments/${appointmentId}/status`, true)}?status=${encodeURIComponent(editedStatus)}`;

      const response = await fetch(url, {
        method: 'PATCH',
        headers: appointmentAuthHeaders(token),
      });

      if (response.ok) {
        const updated = await response.json().catch(() => null);
        setAppointments(prev => prev.map(apt =>
          apt.id === appointmentId
            ? { ...apt, status: updated?.status || editedStatus, confirmation_status: updated?.confirmation_status || apt.confirmation_status }
            : apt
        ));
        setEditingStatusId(null);
        setEditedStatus('');
        showToast('Status updated successfully');
        fetchAppointments();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showToast(`Failed to update status: ${error.message}`, 'error');
      fetchAppointments();
    }
  };

  const confirmDelete = (id) => {
    setDeleteTargetId(id);
    setShowDeleteConfirm(true);
    setActionMenu(null);
  };

  const handleDelete = async () => {
    if (!deleteTargetId) {
      showToast('Invalid appointment ID', 'error');
      return;
    }

    try {
      const token = getToken();
      if (!token) {
        showToast('Not signed in', 'error');
        return;
      }
      const url = buildUrl(API_BASE_URL, `appointments/${deleteTargetId}`, true);

      const response = await fetch(url, {
        method: 'DELETE',
        headers: appointmentAuthHeaders(token),
      });

      if (response.ok) {
        setAppointments(prev => prev.filter(apt => apt.id !== deleteTargetId));
        showToast('Appointment deleted successfully');

        setTimeout(() => fetchAppointments(), 500);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
      showToast(`Failed to delete appointment: ${error.message}`, 'error');
      fetchAppointments();
    } finally {
      setShowDeleteConfirm(false);
      setDeleteTargetId(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      showToast('Please select appointments to delete', 'error');
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    try {
      const token = getToken();
      if (!token) {
        showToast('Not signed in', 'error');
        return;
      }
      const headers = appointmentAuthHeaders(token);
      const deletePromises = selectedRows.map(id =>
        fetch(buildUrl(API_BASE_URL, `appointments/${id}`, true), {
          method: 'DELETE',
          headers,
        })
      );

      const results = await Promise.all(deletePromises);

      const failedCount = results.filter(r => !r.ok).length;

      if (failedCount > 0) {
        showToast(`${selectedRows.length - failedCount} deleted, ${failedCount} failed`, 'error');
      } else {
        showToast(`${selectedRows.length} appointment(s) deleted successfully`);
      }

      setSelectedRows([]);
      fetchAppointments();
    } catch (error) {
      console.error('Error deleting appointments:', error);
      showToast('Failed to delete appointments', 'error');
      fetchAppointments();
    } finally {
      setShowDeleteConfirm(false);
      setDeleteTargetId(null);
    }
  };

  const formatDateTimeForInput = (isoString) => toDatetimeLocalValue(isoString);

  const startEditingAppointment = (appointment) => {
    const duration = defaultDurationMinutes(appointment);
    const startLocal = formatDateTimeForInput(appointment.startTime);
    const endLocal = appointment.endTime
      ? formatDateTimeForInput(appointment.endTime)
      : computeEndFromStartLocal(startLocal, duration);
    setEditingAppointment(appointment);
    const titleResolved = resolveAppointmentTitleMode(appointment.title || 'New Consultation');
    setFormData({
      title: titleResolved.mode === APPOINTMENT_TITLE_CUSTOM
        ? titleResolved.customTitle
        : titleResolved.mode,
      titleMode: titleResolved.mode,
      appointmentType: appointment.appointmentType || appointment.appointment_type || 'In-Person',
      startTime: startLocal,
      endTime: endLocal,
      duration,
      PMRN: appointment.PMRN || '',
      provider_id: isClinicianUser
        ? (lockedProviderId || appointment.provider_id || '')
        : (appointment.provider_id || ''),
      confirmation_notes: appointment.confirmation_notes || '',
    });
    setShowModal(true);
    setActionMenu(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev };
      if (name === 'PMRN' || name === 'provider_id') {
        next[name] = parseInt(value, 10) || '';
      } else if (name === 'titleMode') {
        next.titleMode = value;
        if (value === APPOINTMENT_TITLE_CUSTOM) {
          if (APPOINTMENT_TITLE_PRESETS.includes(next.title)) {
            next.title = '';
          }
        } else {
          next.title = value;
        }
      } else if (name === 'title') {
        next.title = String(value).slice(0, APPOINTMENT_TITLE_MAX_LENGTH);
        next.titleMode = APPOINTMENT_TITLE_CUSTOM;
      } else if (name === 'confirmation_notes') {
        next.confirmation_notes = String(value).slice(0, APPOINTMENT_NOTES_MAX_LENGTH);
      } else if (name === 'duration') {
        const mins = Math.max(5, parseInt(value, 10) || 30);
        next.duration = mins;
        if (next.startTime) {
          next.endTime = computeEndFromStartLocal(next.startTime, mins);
        }
      } else if (name === 'startTime') {
        next.startTime = value;
        const mins = Number(next.duration) || 30;
        next.endTime = computeEndFromStartLocal(value, mins);
      } else if (name === 'endTime') {
        next.endTime = value;
        if (next.startTime && value) {
          const startMs = new Date(next.startTime).getTime();
          const endMs = new Date(value).getTime();
          if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
            next.duration = Math.max(5, Math.round((endMs - startMs) / 60000));
          }
        }
      } else {
        next[name] = value;
      }
      return next;
    });
  };

  const resetForm = () => {
    setFormData({
      title: 'New Consultation',
      titleMode: 'New Consultation',
      appointmentType: 'In-Person',
      startTime: '',
      endTime: '',
      duration: 30,
      PMRN: '',
      provider_id: isClinicianUser ? (lockedProviderId || '') : '',
      confirmation_notes: '',
    });
    setEditingAppointment(null);
    setShowModal(false);
  };

  const openCreateModal = () => {
    setEditingAppointment(null);
    setFormData({
      title: 'New Consultation',
      titleMode: 'New Consultation',
      appointmentType: 'In-Person',
      startTime: '',
      endTime: '',
      duration: 30,
      PMRN: '',
      provider_id: isClinicianUser ? (lockedProviderId || '') : '',
      confirmation_notes: '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const providerId = isClinicianUser
      ? lockedProviderId
      : formData.provider_id;

    const titleValue = String(formData.title || '').trim();
    if (
      !titleValue ||
      !formData.startTime ||
      !formData.PMRN ||
      !providerId
    ) {
      showToast('Please fill all required fields', 'error');
      return;
    }
    if (titleValue.length > APPOINTMENT_TITLE_MAX_LENGTH) {
      showToast(`Title must be ${APPOINTMENT_TITLE_MAX_LENGTH} characters or fewer`, 'error');
      return;
    }

    const startIso = fromDatetimeLocalValue(formData.startTime);
    const endIso =
      fromDatetimeLocalValue(formData.endTime) ||
      fromDatetimeLocalValue(
        computeEndFromStartLocal(formData.startTime, formData.duration || 30)
      );
    if (!startIso || !endIso) {
      showToast('Invalid start or end time', 'error');
      return;
    }
    if (new Date(endIso) <= new Date(startIso)) {
      showToast('End time must be after start time', 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      const token = getToken();
      if (!token) {
        showToast('Not signed in', 'error');
        return;
      }
      const isEditing = !!editingAppointment;
      const url = isEditing
        ? buildUrl(API_BASE_URL, `appointments/${editingAppointment.id}`, true)
        : buildUrl(API_BASE_URL, 'appointments', false);

      const method = isEditing ? 'PUT' : 'POST';

      const notesValue = String(formData.confirmation_notes || '').trim();
      if (notesValue.length > APPOINTMENT_NOTES_MAX_LENGTH) {
        showToast(`Notes must be ${APPOINTMENT_NOTES_MAX_LENGTH} characters or fewer`, 'error');
        return;
      }

      const submitData = {
        title: titleValue,
        appointmentType: formData.appointmentType,
        startTime: startIso,
        endTime: endIso,
        duration: Number(formData.duration) || 30,
        PMRN: parseInt(formData.PMRN, 10),
        provider_id: parseInt(providerId, 10),
        status: isEditing ? (editingAppointment.status || 'Confirmed') : 'Confirmed',
        confirmation_notes: notesValue || null,
      };

      const response = await fetch(url, {
        method,
        headers: appointmentAuthHeaders(token),
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        showToast(isEditing ? 'Appointment updated successfully' : 'Appointment created successfully');

        fetchAppointments();
        resetForm();
      } else {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail;
        const code = typeof detail === 'string' ? detail : null;
        throw new Error(
          (code && APPOINTMENT_SAVE_ERRORS[code]) ||
          (typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => d.msg || JSON.stringify(d)).join(', ')
              : `HTTP ${response.status}`)
        );
      }
    } catch (error) {
      console.error('Error saving appointment:', error);
      showToast(`Failed to save appointment: ${error.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openZoomForAppointment = useCallback(
    async (appointment) => {
      if (!appointment?.id) return;
      setJoiningSessionId(appointment.id);
      try {
        await startZoomForAppointment({
          apiBaseUrl: API_BASE_URL,
          appointment,
          navigate,
          onBlocked: (reason) => showToast(reason, 'error'),
          onError: (reason) => showToast(reason, 'error'),
        });
      } finally {
        setJoiningSessionId(null);
      }
    },
    [API_BASE_URL, navigate, showToast]
  );

  const actionMenuAppointment = actionMenu ? appointments.find((x) => x.id === actionMenu.id) : null;

  const EditableStatus = ({ appointment }) => {
    const isEditing = editingStatusId === appointment.id;

    if (isEditing) {
      return (
        <div className="flex items-center space-x-2">
          <select
            value={editedStatus}
            onChange={(e) => setEditedStatus(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            autoFocus
          >
            {statusOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <button
            onClick={() => saveStatus(appointment.id)}
            className="text-green-600 hover:text-green-800"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            onClick={cancelEditingStatus}
            className="text-red-600 hover:text-red-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center space-x-2">
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[appointment.status]}`}>
          {appointment.status}
        </span>
        <button
          onClick={() => startEditingStatus(appointment)}
          className="text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Edit status"
        >
          <Edit2 className="w-3 h-3" />
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen vitra-atmosphere">

      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Appointments & Teleconsultation</h1>
          <button
            onClick={openCreateModal}
            className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add New Appointment</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <button
            type="button"
            onClick={() => {
              setStatusFilter('active');
              setSelectedRows([]);
            }}
            className={`w-full text-left rounded-lg transition ring-offset-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              statusFilter === 'active' ? 'ring-2 ring-teal-500' : ''
            }`}
          >
            <StatsCard title="Upcoming" value={activeAppointments.length} icon={<Calendar className="w-8 h-8 text-teal-600" />} />
          </button>
          <button
            type="button"
            onClick={() => {
              setStatusFilter('Completed');
              setSelectedRows([]);
            }}
            className={`w-full text-left rounded-lg transition ring-offset-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              statusFilter === 'Completed' ? 'ring-2 ring-teal-500' : ''
            }`}
          >
            <StatsCard title="Completed / Expired" value={getStatusCount('Completed')} icon={<Check className="w-8 h-8 text-slate-600" />} />
          </button>
          <StatsCard title="Pending requests" value={pendingRequests.length} icon={<Clock className="w-8 h-8 text-amber-600" />} />
          <button
            type="button"
            onClick={() => {
              setStatusFilter('Cancelled');
              setSelectedRows([]);
            }}
            className={`w-full text-left rounded-lg transition ring-offset-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              statusFilter === 'Cancelled' ? 'ring-2 ring-teal-500' : ''
            }`}
          >
            <StatsCard title="Cancelled" value={getStatusCount('Cancelled')} icon={<X className="w-8 h-8 text-red-600" />} />
          </button>
        </div>

        <PendingAppointmentRequests
          requests={pendingRequests}
          patients={patients}
          loading={pendingLoading}
          highlightId={highlightRequestId}
          apiBaseUrl={API_BASE_URL}
          onRefresh={fetchPendingRequests}
          onChanged={() => {
            fetchPendingRequests();
            fetchAppointments();
          }}
          onNotify={showToast}
        />
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-semibold text-gray-900">{statusFilterLabel}</h2>
              {selectedRows.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center space-x-2 px-3 py-1 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete ({selectedRows.length})</span>
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setSelectedRows([]);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                aria-label="Filter appointments by status"
              >
                {statusFilterOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <button
                onClick={() => {
                  fetchAppointments();
                  fetchPendingRequests();
                }}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                title="Refresh appointments"
                disabled={isLoading}
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          <div className="overflow-x-auto overflow-y-visible relative">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedRows.length === filteredAppointments.length && filteredAppointments.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                  </th>
                  <TableHead title="Patient Name" />
                  <TableHead title="Status" />
                  <TableHead title="Clinician" />
                  <TableHead title="Date" />
                  <TableHead title="Time" />
                  <TableHead title="Type" />
                  <TableHead title="Action" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 min-h-[300px]">
                {isLoading ? (
                  <tr>
                    <td colSpan="8" className="h-[300px]">
                      <div className="flex flex-col items-center justify-center h-full gap-3">
                        <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
                        <p className="text-gray-600 font-medium">Loading appointments...</p>
                      </div>
                    </td>
                  </tr>
                ) : patientEntries.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      {searchTerm
                        ? 'No appointments match your search'
                        : `No ${statusFilterLabel.toLowerCase()} appointments found`}
                    </td>
                  </tr>
                ) : (
                  patientEntries.map(patientEntry => {
                    const showAll = expandedPatients[patientEntry.patientName];
                    const mainAppt = patientEntry.nextAppointment;
                    const extraAppts = patientEntry.appointments
                      .filter(a => a.id !== mainAppt.id)
                      .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

                    return (
                      <React.Fragment key={patientEntry.patientName}>
                        <tr key={mainAppt.id} className="hover:bg-gray-50 group">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedRows.includes(mainAppt.id)}
                              onChange={() => handleRowSelect(mainAppt.id)}
                              className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                            />
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <span className="truncate">{mainAppt.patient_name || "N/A"}</span>
                              {extraAppts.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedPatients(prev => ({
                                    ...prev,
                                    [patientEntry.patientName]: !prev[patientEntry.patientName]
                                  }))}
                                  className={`inline-flex items-center gap-1 shrink-0 rounded-md border px-2 py-1 text-[11px] font-medium tracking-wide transition-colors ${
                                    showAll
                                      ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-white'
                                  }`}
                                  title={showAll ? 'Hide prior visits' : `Show ${extraAppts.length} prior visit${extraAppts.length > 1 ? 's' : ''}`}
                                  aria-expanded={!!showAll}
                                >
                                  {showAll ? (
                                    <>
                                      <ChevronUp className="w-3.5 h-3.5" />
                                      Prior visits
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="w-3.5 h-3.5" />
                                      {extraAppts.length} prior
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <EditableStatus appointment={mainAppt} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {mainAppt.provider_name || "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {mainAppt.startTime && mainAppt.startTime.split('T')[0]}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {mainAppt.startTime && mainAppt.startTime.split('T')[1] && mainAppt.startTime.split('T')[1].substring(0, 5)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {appointmentTypeOf(mainAppt) || '—'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2">
                              {canJoinVideoAppointment(mainAppt) && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openZoomForAppointment(mainAppt);
                                  }}
                                  disabled={joiningSessionId === mainAppt.id}
                                  className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1.5 text-xs font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-50"
                                  title="Join video consultation"
                                >
                                  <Video className="w-3.5 h-3.5" />
                                  <span>{joiningSessionId === mainAppt.id ? 'Joining…' : 'Join'}</span>
                                </button>
                              )}
                              <button
                                type="button"
                                data-action-menu-trigger={mainAppt.id}
                                aria-haspopup="menu"
                                aria-expanded={actionMenu?.id === mainAppt.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setActionMenu((prev) =>
                                    prev?.id === mainAppt.id
                                      ? null
                                      : { id: mainAppt.id, ...computeActionMenuPosition(rect) }
                                  );
                                }}
                                className={`rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 ${actionMenu?.id === mainAppt.id ? 'bg-gray-100 text-gray-600' : ''}`}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {extraAppts.length > 0 && showAll && (
                          <tr className="bg-slate-50/80">
                            <td colSpan="8" className="px-6 pb-4 pt-0">
                              <div className="ml-8 overflow-hidden rounded-lg border border-gray-200 bg-white">
                                <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2.5">
                                  <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      Prior visits
                                    </p>
                                    <p className="text-sm text-gray-800">
                                      {patientEntry.patientName}
                                      <span className="text-gray-400"> · </span>
                                      {extraAppts.length} earlier appointment{extraAppts.length === 1 ? '' : 's'}
                                    </p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setExpandedPatients(prev => ({
                                      ...prev,
                                      [patientEntry.patientName]: false
                                    }))}
                                    className="text-xs font-medium text-gray-500 hover:text-gray-800"
                                  >
                                    Collapse
                                  </button>
                                </div>
                                <ul className="divide-y divide-gray-100">
                                  {extraAppts.map((appointment) => {
                                    const timeOnly =
                                      (appointment.startTime || '').split('T')[1]?.substring(0, 5) || '—';
                                    const dateOnly =
                                      appointment.startTime ? appointment.startTime.split('T')[0] : '—';
                                    const typeLabel = appointmentTypeOf(appointment) || 'Appointment';
                                    const showJoin = canJoinVideoAppointment(appointment);

                                    return (
                                      <li
                                        key={appointment.id}
                                        className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                                      >
                                        <div className="min-w-0 flex-1">
                                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                            <span className="text-sm font-medium text-gray-900 tabular-nums">
                                              {dateOnly}
                                            </span>
                                            <span className="text-sm text-gray-500 tabular-nums">
                                              {timeOnly}
                                            </span>
                                            <span className="text-sm text-gray-600">{typeLabel}</span>
                                            <span
                                              className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                                                statusColors[appointment.status] || 'bg-gray-100 text-gray-700'
                                              }`}
                                            >
                                              {appointment.status}
                                            </span>
                                          </div>
                                          <p className="mt-1 text-xs text-gray-500">
                                            Clinician: {appointment.provider_name || 'N/A'}
                                          </p>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                                          {showJoin && (
                                            <button
                                              type="button"
                                              onClick={() => openZoomForAppointment(appointment)}
                                              disabled={joiningSessionId === appointment.id}
                                              className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1.5 text-xs font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-50"
                                              title="Join video consultation"
                                            >
                                              <Video className="w-3.5 h-3.5" />
                                              <span>{joiningSessionId === appointment.id ? 'Joining…' : 'Join'}</span>
                                            </button>
                                          )}
                                          {isVideoAppointmentType(appointment) && (
                                            <button
                                              type="button"
                                              onClick={() => openTranscriptViewer(appointment)}
                                              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                            >
                                              <FileText className="w-3.5 h-3.5" />
                                              Transcript
                                            </button>
                                          )}
                                          <button
                                            type="button"
                                            onClick={() => startEditingAppointment(appointment)}
                                            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                          >
                                            <Edit2 className="w-3.5 h-3.5" />
                                            Edit
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => confirmDelete(appointment.id)}
                                            className="inline-flex items-center justify-center rounded-lg border border-red-100 bg-red-50 px-2 py-1.5 text-red-600 hover:bg-red-100"
                                            aria-label="Delete appointment"
                                            title="Delete appointment"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <Modal
        open={showModal}
        onClose={() => { if (!isSubmitting) resetForm(); }}
        align="start"
      >
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-2xl w-full max-h-[calc(100vh-6rem)] sm:max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col">
            <DialogHeader
              title={editingAppointment ? 'Edit Appointment' : 'Add New Appointment'}
              subtitle={editingAppointment ? 'Update schedule, patient, and visit details.' : 'Schedule a visit with patient and clinician details.'}
              onClose={() => { if (!isSubmitting) resetForm(); }}
              tone="muted"
              sticky
              className={isSubmitting ? '[&_button]:opacity-50 [&_button]:pointer-events-none' : ''}
            />

            <DialogBody className="px-6 py-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="titleMode"
                    value={formData.titleMode}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    {titleOptions.map((title) => (
                      <option key={title} value={title}>{title}</option>
                    ))}
                    <option value={APPOINTMENT_TITLE_CUSTOM}>Custom title…</option>
                  </select>
                  {formData.titleMode === APPOINTMENT_TITLE_CUSTOM && (
                    <div className="mt-2">
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleFormChange}
                        maxLength={APPOINTMENT_TITLE_MAX_LENGTH}
                        placeholder="Enter appointment title"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                      <p className="mt-1 text-xs text-gray-500 text-right">
                        {String(formData.title || '').length}/{APPOINTMENT_TITLE_MAX_LENGTH}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Appointment Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="appointmentType"
                    value={formData.appointmentType}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    {appointmentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (minutes)
                  </label>
                  <select
                    name="duration"
                    value={formData.duration}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    {[15, 20, 30, 45, 60, 90, 120].map((m) => (
                      <option key={m} value={m}>{m} min</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Patient <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="PMRN"
                    value={formData.PMRN}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="">Select Patient</option>
                    {patients.map(patient => (
                      <option key={patient.MRN} value={patient.MRN}>
                        {patient.firstName} {patient.lastName}
                      </option>

                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Clinician <span className="text-red-500">*</span>
                  </label>
                  {isClinicianUser ? (
                    <input
                      type="text"
                      value={clinicianDisplayName || 'Loading…'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-800"
                    />
                  ) : (
                    <select
                      name="provider_id"
                      value={formData.provider_id}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value="">Select Clinician</option>
                      {clinicians.map((clinician) => {
                        const fullName =
                          clinician.name ||
                          `${clinician.firstName || ''} ${clinician.lastName || ''}`.trim() ||
                          `Clinician ${clinician.id}`;
                        const roleLabel = clinician.role || clinician.speciality || 'Care Team';
                        return (
                          <option key={clinician.id} value={clinician.id}>
                            {fullName} — {roleLabel}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="confirmation_notes"
                    rows={3}
                    value={formData.confirmation_notes}
                    onChange={handleFormChange}
                    maxLength={APPOINTMENT_NOTES_MAX_LENGTH}
                    placeholder="Optional notes for this appointment"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-y min-h-[88px]"
                  />
                  <p className="mt-1 text-xs text-gray-500 text-right">
                    {String(formData.confirmation_notes || '').length}/{APPOINTMENT_NOTES_MAX_LENGTH}
                  </p>
                </div>
              </div>
            </DialogBody>

            <DialogFooter>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                disabled={isSubmitting}
              >
                {isSubmitting && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>{editingAppointment ? 'Update Appointment' : 'Create Appointment'}</span>
              </button>
            </DialogFooter>
          </div>
      </Modal>

      <Modal
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeleteTargetId(null);
        }}
      >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-red-100 rounded-full p-2">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Appointment{selectedRows.length > 1 ? 's' : ''}</h3>
              </div>
              <p className="text-gray-600 mb-6">
                {deleteTargetId
                  ? 'Are you sure you want to delete this appointment? This action cannot be undone.'
                  : `Are you sure you want to delete ${selectedRows.length} appointment(s)? This action cannot be undone.`
                }
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteTargetId(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={deleteTargetId ? handleDelete : confirmBulkDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
      </Modal>

      <Modal open={!!transcriptModal} onClose={closeTranscriptModal}>
          {transcriptModal && (
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 max-w-3xl w-full max-h-[min(90vh,720px)] flex flex-col overflow-hidden">
            <DialogHeader
              title="Visit transcript"
              subtitle={[
                transcriptModal.appointment?.patient_name || 'Patient',
                transcriptModal.appointment?.title || 'Appointment',
                transcriptModal.appointment?.startTime
                  ? new Date(transcriptModal.appointment.startTime).toLocaleString()
                  : null,
              ].filter(Boolean).join(' · ')}
              onClose={closeTranscriptModal}
              tone="muted"
            />

            <DialogBody className="px-6 py-4">
              {transcriptModal.loading && (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-500">
                  <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
                  <p className="text-sm">Loading transcript…</p>
                </div>
              )}
              {!transcriptModal.loading && transcriptModal.error && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  {transcriptModal.error}
                </div>
              )}
              {!transcriptModal.loading && !transcriptModal.error && transcriptModal.data && (
                <>
                  {!transcriptModal.data.fullTranscript?.trim() ? (
                    <p className="text-sm text-gray-500">No transcript text has been saved for this visit yet.</p>
                  ) : (
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed bg-gray-50/80 rounded-xl border border-gray-100 p-4">
                      {transcriptModal.data.fullTranscript}
                    </pre>
                  )}
                </>
              )}
            </DialogBody>

            <DialogFooter>
              <button
                type="button"
                onClick={closeTranscriptModal}
                className="px-4 py-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-white text-sm font-medium"
              >
                Close
              </button>
              <button
                type="button"
                onClick={downloadTranscriptFile}
                disabled={!transcriptModal.data?.fullTranscript?.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                Download .txt
              </button>
            </DialogFooter>
          </div>
          )}
      </Modal>

      {createPortal(
        <DropdownPanel
          open={Boolean(actionMenu && actionMenuAppointment)}
          transformOrigin="top right"
          data-appointment-action-menu
          role="menu"
          aria-label="Appointment actions"
          className="fixed z-popover flex flex-col overflow-x-hidden overflow-y-auto rounded-xl border border-gray-200/90 bg-white py-1 shadow-lg shadow-gray-900/10 ring-1 ring-black/[0.04]"
          style={
            actionMenu
              ? {
                  top: actionMenu.top,
                  left: actionMenu.left,
                  width: actionMenu.width,
                  minWidth: actionMenu.width,
                  maxHeight: actionMenu.maxHeight,
                }
              : undefined
          }
        >
          {actionMenuAppointment && (
            <>
              {canJoinVideoAppointment(actionMenuAppointment) && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    const appt = actionMenuAppointment;
                    setActionMenu(null);
                    openZoomForAppointment(appt);
                  }}
                  className="flex w-full items-center space-x-2 border-b border-gray-100 px-4 py-2.5 text-left text-sm text-teal-800 hover:bg-teal-50"
                >
                  <Video className="h-4 w-4 shrink-0" />
                  <span>Join consultation</span>
                </button>
              )}
              {isVideoAppointmentType(actionMenuAppointment) && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setActionMenu(null);
                    openTranscriptViewer(actionMenuAppointment);
                  }}
                  className="flex w-full items-center space-x-2 border-b border-gray-100 px-4 py-2.5 text-left text-sm text-teal-800 hover:bg-teal-50"
                >
                  <FileText className="h-4 w-4 shrink-0" />
                  <span>View transcript</span>
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setActionMenu(null);
                  startEditingAppointment(actionMenuAppointment);
                }}
                className="flex w-full items-center space-x-2 px-4 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50"
              >
                <Edit2 className="h-4 w-4 shrink-0" />
                <span>Edit</span>
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setActionMenu(null);
                  confirmDelete(actionMenuAppointment.id);
                }}
                className="flex w-full items-center space-x-2 px-4 py-2.5 text-left text-sm text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 shrink-0" />
                <span>Delete</span>
              </button>
            </>
          )}
        </DropdownPanel>,
        document.body
      )}

      <Footer />
    </div>
  );
};

const StatsCard = ({ title, value, icon }) => (
  <div className="bg-white p-6 rounded-lg shadow-sm border transition-[box-shadow] duration-200 hover:shadow-md">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-600 text-sm">{title}</p>
        <p className="text-2xl font-bold text-gray-900">
          <AnimatedNumber value={value} />
        </p>
      </div>
      {icon}
    </div>
  </div>
);

const TableHead = ({ title }) => (
  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    {title}
  </th>
);

export default AppointmentsDashboard;
